import { Eye, EyeOff } from "lucide-react";
import React, { useId, useState } from "react";
import type {
  Control,
  FieldValues,
  Path,
  RegisterOptions,
} from "react-hook-form";
import { Controller } from "react-hook-form";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface InputProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  type?:
    | "date"
    | "datetime-local"
    | "email"
    | "hidden"
    | "month"
    | "number"
    | "password"
    | "search"
    | "tel"
    | "text"
    | "time"
    | "url"
    | "week";
  icon?: React.ReactNode;
  endIcon?: React.ReactNode;
  placeholder?: string;
  label?: string;
  size?: "1" | "2" | "3";
  radius?: "none" | "small" | "medium" | "large" | "full";
  color?: string;
  className?: string;
  extraInfo?: string;
  variant?: "classic" | "surface" | "soft" | "mix";
  rules?: Omit<
    RegisterOptions<T, Path<T>>,
    "disabled" | "setValueAs" | "valueAsNumber" | "valueAsDate"
  >;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isLoading?: boolean;
  inputClassName?: string;
  disabled?: boolean;
  require?: boolean;
  /** Optional explicit id; auto-generated if omitted. Used for label/error association. */
  id?: string;
  /** Show a built-in show/hide toggle when type="password". Defaults to true for password. */
  showPasswordToggle?: boolean;
  /** Optional autoComplete hint (e.g., "email", "current-password", "new-password"). */
  autoComplete?: string;
  /** inputMode hint for mobile keyboards (e.g., "numeric"). */
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  /** Key down handler (e.g., to block non-numeric characters). */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

// Sizing and radius are handled by the global Input component.
// These maps are kept only for backward-compat if consumers pass size/radius props.
const _sizeMap: Record<string, string> = {
  "1": "",
  "2": "",
  "3": "",
};

const _radiusMap: Record<string, string> = {
  none: "",
  small: "",
  medium: "",
  large: "",
  full: "",
};

const TextInputControl = <T extends FieldValues>({
  name,
  control,
  type = "text",
  icon,
  endIcon,
  label,
  placeholder,
  radius: _radius = "small",
  color: _color,
  className,
  size = "3",
  extraInfo,
  variant: _variant = "surface",
  rules,
  isLoading: _isLoading,
  disabled,
  inputClassName,
  require,
  id,
  showPasswordToggle = true,
  autoComplete,
  inputMode,
  onKeyDown,
  ...props
}: InputProps<T>) => {
  const generatedId = useId();
  const inputId = id ?? `${name}-${generatedId}`;
  const errorId = `${inputId}-error`;
  const helpId = `${inputId}-help`;
  const [showPassword, setShowPassword] = useState(false);

  const isPassword = type === "password";
  const showToggle = isPassword && showPasswordToggle && !endIcon;
  const effectiveType = isPassword && showPassword ? "text" : type;

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement>,
    onBlur: () => void,
  ) => {
    onBlur();
    props.onBlur?.(e);
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({
        field: { onChange, onBlur, value, ref },
        fieldState: { error },
      }) => {
        const describedBy = error ? errorId : extraInfo ? helpId : undefined;
        return (
          <div className={className}>
            {label && (
              <label htmlFor={inputId} className="typo-label text-text-primary">
                {label} {require && <span className="text-red-500">*</span>}
              </label>
            )}
            <div className="relative pt-1">
              <div className="relative flex items-center">
                {icon && (
                  <span className="absolute left-3 flex items-center text-muted-foreground">
                    {icon}
                  </span>
                )}
                <Input
                  id={inputId}
                  placeholder={placeholder}
                  className={cn(
                    icon && "pl-10",
                    (endIcon || showToggle) && "pr-10",
                    inputClassName,
                  )}
                  type={effectiveType}
                  value={value || ""}
                  disabled={disabled}
                  autoComplete={autoComplete}
                  inputMode={inputMode}
                  onKeyDown={onKeyDown}
                  aria-describedby={describedBy}
                  onChange={(e) => {
                    onChange(e);
                    props.onChange?.(e);
                  }}
                  onBlur={(e) => handleBlur(e, onBlur)}
                  ref={ref}
                />
                {endIcon && (
                  <button
                    type="button"
                    aria-label="Field action"
                    className="absolute right-3 flex items-center text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-9 focus-visible:ring-offset-1 rounded"
                  >
                    {endIcon}
                  </button>
                )}
                {showToggle && (
                  <button
                    type="button"
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                    aria-pressed={showPassword}
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-3 flex items-center text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-9 focus-visible:ring-offset-1 rounded"
                  >
                    {showPassword ? (
                      <EyeOff size={16} aria-hidden="true" />
                    ) : (
                      <Eye size={16} aria-hidden="true" />
                    )}
                  </button>
                )}
              </div>
            </div>

            {error ? (
              <div className="flex items-center gap-1 py-1">
                <span
                  id={errorId}
                  role="alert"
                  aria-live="polite"
                  className="text-extrasmall text-red-500"
                >
                  {error?.message}
                </span>
              </div>
            ) : (
              extraInfo && (
                <div className="flex items-center gap-1 py-2">
                  <span
                    id={helpId}
                    className="text-text-secondary-inverse text-extrasmall"
                  >
                    {extraInfo}
                  </span>
                </div>
              )
            )}
          </div>
        );
      }}
    />
  );
};

export default TextInputControl;

export { TextInputControl };
